/* -----------------UI--------------- */
import { useTranslation } from "react-i18next";
import SessionConfig from "../screens/SessionConfig";

import { useSession } from "@features/session/";
/* -----------------Helpers & Hooks--------------- */
import { getParentMetadata } from "@helpers/content/metadata";
import { groupTagsByType, hasProductAccess } from "@helpers/content/tag";
import { router, useLocalSearchParams } from "expo-router";

import { isMockedData } from "@constants/demo";
import { diagnosticItems } from "@constants/mocked-data";
import { events } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import { diagnosticTagIds } from "@helpers/session";
import useAnalytics from "@hooks/useAnalytics";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { useProductMetadata } from "@hooks/useProduct";
import { isDev, isStaging } from "@memorang/constants";
/* -----------------Types--------------- */
import type { Distribution, MixedItemTag } from "@memorang/types";
import { Loader, MicrophonePermissionDialog } from "@memorang/ui";
import { ConfirmDialog } from "components/ConfirmDialog";
import { Audio } from "expo-av";
import { useState } from "react";
import {
	listeningItemsPerSet,
	readingItemsPerSet,
	speakingItemsPerSet,
	writingItemsPerSet,
} from "../constants";
import { tagIds } from "../helpers/learningModes";

const getSets = (tags: MixedItemTag[]) => {
	const readingTag = tags.find((tag) => Number(tag.id) === tagIds.reading);
	const writingTag = tags.find((tag) => Number(tag.id) === tagIds.writing);
	const listeningTag = tags.find((tag) => Number(tag.id) === tagIds.listening);
	const speakingTag = tags.find((tag) => Number(tag.id) === tagIds.speaking);

	const numReading = readingTag?.numItems || 0;
	const numWriting = writingTag?.numItems || 0;
	const numListening = listeningTag?.numItems || 0;
	const numSpeaking = speakingTag?.numItems || 0;
	return {
		[diagnosticTagIds.readingListening]: {
			sets: 2,
			items: 10,
			forEstimateItems: 10,
			forEstimateSets: 2,
		},
		[diagnosticTagIds.speakingWriting]: {
			sets: 2,
			items: 10,
			forEstimateItems: 10,
			forEstimateSets: 2,
		},
		[tagIds.reading]: {
			sets: Math.ceil(numReading / readingItemsPerSet),
			items: numReading,
			forEstimateItems: readingItemsPerSet,
			forEstimateSets: 1,
		},
		[tagIds.writing]: {
			sets: Math.ceil(numWriting / writingItemsPerSet),
			items: numWriting,
			forEstimateItems: writingItemsPerSet,
			forEstimateSets: 1,
		},
		[tagIds.listening]: {
			sets: Math.ceil(numListening / listeningItemsPerSet),
			items: numListening,
			forEstimateItems: listeningItemsPerSet,
			forEstimateSets: 1,
		},
		[tagIds.speaking]: {
			sets: Math.ceil(numSpeaking / speakingItemsPerSet),
			items: numSpeaking,
			forEstimateItems: speakingItemsPerSet,
			forEstimateSets: 1,
		},
	};
};
const SessionConfigContainer = ({
	propSelectedTagIds,
	toggleShowPurchaseDialog,
	handleClose,
}: {
	propSelectedTagIds?: string[];
	toggleShowPurchaseDialog?: () => void;
	handleClose?: () => void;
}) => {
	const { t } = useTranslation();
	const { selectedTagIds } = useLocalSearchParams<{
		selectedTagIds: string[];
		studymore: string;
	}>();

	const {
		showErrorDialog,
		toggleShowErrorDialog,
		isCreatingSession,
		handleCreateFormativeSession,
	} = useSession();

	const finalSelectedTagIds = propSelectedTagIds || selectedTagIds || [];

	useAnalytics({
		eventName: events.sessionConfigOpened,
		selectedTagIds: finalSelectedTagIds
			? JSON.stringify(finalSelectedTagIds)
			: "",
	});

	const { data: mixedItemDistResponse, isLoading } = useMixedItemDist(true);

	const { data: productMetadata, isLoading: isProductMetadataLoading } =
		useProductMetadata();

	const [showPermissionDialog, setShowPermissionDialog] = useState(false);

	const data = mixedItemDistResponse;

	if (isLoading || !data || isProductMetadataLoading) {
		return <Loader />;
	}

	//TODO:Abstract this into a function
	const { contents, tags, freeTagIds } = data;
	const numItems = contents.reduce((a, c) => a + c.totalCount, 0);

	const { distribution } = getParentMetadata(contents, numItems);
	const hasAccess =
		(isDev && !isStaging) || isMockedData
			? true
			: hasProductAccess(productMetadata);
	// const hasAccess = true;
	const tagsByType = groupTagsByType(tags, "Filter", freeTagIds, hasAccess);

	const sectionTags = [
		{
			title: t("sessionConfig.skill"),
			tags: [...Object.entries(tagsByType).flatMap(([_title, tags]) => tags)],
		},
	];

	const handleCreateSession = async (dist: Distribution, tags: number[]) => {
		const mappedTags = tags.map((tag) => Number(tag));

		if (mappedTags.includes(tagIds.speaking)) {
			const current = await Audio.getPermissionsAsync();
			if (current.status !== "granted") {
				const newPermission = await Audio.requestPermissionsAsync();
				if (newPermission.status !== "granted") {
					setShowPermissionDialog(true);
					return;
				}
			}
		}

		handleCreateFormativeSession({
			dist,
			tags: mappedTags,
			handleClose,
			itemIds: [],
		});
	};

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: "STUDY_PACK",
				},
			});
		}
	};

	return (
		<>
			<SessionConfig
				available={distribution}
				creatingSession={isCreatingSession}
				handleCreateSession={handleCreateSession}
				handleShowPurchase={handleShowPurchase}
				handleClose={handleClose}
				hasAccess={hasAccess}
				diagnosticItems={diagnosticItems}
				setsAndItems={getSets(tags)}
				exams={mixedItemDistResponse?.exams}
				additionalParams={{
					tagsByType,
					freeTagIds,
					hasAccess,
					sectionTags,
					selectedTagIds: finalSelectedTagIds,
				}}
			/>

			<MicrophonePermissionDialog
				showDialog={showPermissionDialog}
				setShowDialog={() => {
					setShowPermissionDialog(false);
				}}
			/>

			{showErrorDialog && (
				<ConfirmDialog
					visible
					title={t("errors.sessionCreation.title")}
					body={t("errors.sessionCreation.body")}
					dismissCta="Ok"
					dismissCallback={toggleShowErrorDialog}
				/>
			)}
		</>
	);
};

export default SessionConfigContainer;
